import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiRequest } from '../utils/api';
import { toast } from 'react-hot-toast';
import { ArrowLeft } from 'lucide-react';
import { ThemeToggle } from '../contexts/ThemeContext';
import useIsMobile from '../hooks/useIsMobile';
import DocxsAiLogo from '../components/shared/DocxsAiLogo';

const AUTH_PAGE_IMAGE = '/authpage.jpg';

const AuthShell = ({ children }) => {
  const isMobile = useIsMobile(1024);

  return (
    <div className="min-h-screen flex bg-[var(--color-bg)]">
      <div
        className={`relative flex-1 flex flex-col justify-center items-center bg-[var(--color-bg)] py-12 px-4 ${
          isMobile ? 'w-full' : 'max-w-md flex-shrink-0 px-8'
        }`}
      >
        <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Home
          </Link>
          <ThemeToggle compact />
        </div>
        <DocxsAiLogo className="h-16 w-auto mb-8" />
        <div className="w-full max-w-[400px]">{children}</div>
      </div>
      {!isMobile && (
        <div
          className="flex-1 bg-cover bg-center bg-no-repeat min-h-screen"
          style={{ backgroundImage: `url(${AUTH_PAGE_IMAGE})` }}
        />
      )}
    </div>
  );
};

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [tokenValid, setTokenValid] = useState(null); // null = loading, true = valid, false = invalid
  const [tokenError, setTokenError] = useState('');

  useEffect(() => {
    if (!token) {
      toast.error('Invalid reset link');
      navigate('/login');
      return;
    }
    // Check token validity
    const checkToken = async () => {
      try {
        setTokenValid(null);
        setTokenError('');
        const res = await apiRequest(`/auth/reset-password-link?token=${encodeURIComponent(token)}`);
        if (res && res.detail === 'Valid') {
          setTokenValid(true);
        } else {
          setTokenValid(false);
          setTokenError('Invalid Link! You have already used this link once and can\'t re-use it. Kindly request another via the Forgot Password link on login page.');
        }
      } catch (err) {
        setTokenValid(false);
        setTokenError('Invalid Link! You have already used this link once and can\'t re-use it. Kindly request another via the Forgot Password link on login page.');
      }
    };
    checkToken();
  }, [token, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/(?=.*\d)/.test(password)) {
      return 'Password must contain at least one number';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.newPassword || !formData.confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    const passwordError = validatePassword(formData.newPassword);
    if (passwordError) {
      toast.error(passwordError);
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await apiRequest('/auth/reset-password', {
        method: 'POST',
        body: {
          token: token,
          new_password: formData.newPassword
        },
      });

      if (response.success !== false) {
        setIsSuccess(true);
        toast.success('Password reset successful!');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        toast.error(response.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      if (error.message.includes('Invalid') || error.message.includes('expired')) {
        setTokenValid(false);
        setTokenError('Invalid Link! You have already used this link once and can\'t re-use it. Kindly request another via the Forgot Password link on login page.');
      } else {
        toast.error('Failed to reset password. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <AuthShell>
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-[var(--color-primary-soft)]">
            <svg className="h-8 w-8 text-[var(--color-success)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-[var(--color-text)]">
            Password reset successful!
          </h2>
          <p className="mt-2 text-sm text-[var(--color-text-muted)]">
            Your password has been successfully reset. You can now log in with your new password.
          </p>
          <p className="mt-4 text-sm text-[var(--color-text-muted)]">
            Redirecting to login page...
          </p>
          <div className="mt-6">
            <button
              onClick={() => navigate('/login')}
              className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium brand-gradient-btn focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
            >
              Go to login
            </button>
          </div>
        </div>
      </AuthShell>
    );
  }

  if (tokenValid === false) {
    return (
      <AuthShell>
        <div className="text-center">
          <h2 className="mt-6 text-2xl font-bold text-[var(--color-danger)]">
            Invalid or Expired Link
          </h2>
          <p className="mt-2 text-sm text-[var(--color-text-muted)]">
            {tokenError}
          </p>
          <div className="mt-6">
            <button
              onClick={() => navigate('/login')}
              className="w-full brand-gradient-btn py-2.5 rounded-lg font-semibold"
            >
              Go to Login
            </button>
          </div>
        </div>
      </AuthShell>
    );
  }

  if (tokenValid === null) {
    return (
      <AuthShell>
        <div className="text-center text-[var(--color-text-secondary)] text-lg">
          Checking link validity...
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="text-center">
        <h2 className="mt-2 text-3xl font-extrabold text-[var(--color-text)]">
          Reset your password
        </h2>
        <p className="mt-2 text-sm text-[var(--color-text-muted)]">
          Enter your new password below.
        </p>
      </div>
      <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-[var(--color-text-secondary)]">
              New Password
            </label>
            <div className="mt-1 relative">
              <input
                id="newPassword"
                name="newPassword"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                required
                value={formData.newPassword}
                onChange={handleChange}
                className="themed-input appearance-none relative block w-full px-3 py-2.5 pr-10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] focus:border-[var(--color-primary)] sm:text-sm"
                placeholder="Enter new password"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {showPassword ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  )}
                </svg>
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-[var(--color-text-secondary)]">
              Confirm New Password
            </label>
            <div className="mt-1 relative">
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                className="themed-input appearance-none relative block w-full px-3 py-2.5 pr-10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] focus:border-[var(--color-primary)] sm:text-sm"
                placeholder="Confirm new password"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {showConfirmPassword ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full brand-gradient-btn py-2.5 sm:py-3 px-4 rounded-lg text-sm sm:text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center mt-4"
        >
          {isLoading ? (
            <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-[var(--color-text-inverse)] border-t-transparent rounded-full animate-spin" />
          ) : (
            'Reset password'
          )}
        </button>
        <button
          type="button"
          className="w-full mt-2 bg-[var(--color-surface-muted)] text-[var(--color-text)] border border-[var(--color-border)] py-2.5 sm:py-3 px-4 rounded-lg text-sm sm:text-base font-semibold hover:opacity-90 transition-colors flex items-center justify-center"
          onClick={() => navigate('/login')}
          disabled={isLoading}
        >
          Cancel
        </button>

        <div className="mt-6 text-left text-[var(--color-text-muted)] text-xs">
          <div className="font-semibold mb-1 text-[var(--color-text-secondary)]">Password requirements:</div>
          <ul className="list-disc ml-5">
            <li>At least 8 characters long</li>
            <li>Contains uppercase and lowercase letters</li>
            <li>Contains at least one number</li>
          </ul>
        </div>
      </form>
    </AuthShell>
  );
};

export default ResetPasswordPage;
